<template>
    <div class="modal fade" id="yearbook_pose">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content border-none shadow-xl">
                <div class="flex items-center justify-between bg-gray-100 py-2 px-2 rounded-t-md">
                    <h4 class="m-0">Change {{capitalize(change.type)}} Pose</h4>
                    <div class="text-right mr-2 text-gray-700 hover:text-gray-900 hover:underline cursor-pointer lg:hidden" data-dismiss="modal">
                        Close <font-awesome-icon icon="xmark"></font-awesome-icon>
                    </div>
                </div>
                <div class="modal-body p-3">
                    <p class="text-center font-bold mb-3" v-if="pastCutoff">NO CHANGES CAN BE SAVED, CUTOFF DATE PASSED</p>
                    <div class="flex flex-col">
                        <div class="flex flex-row">
                            <figure class="mb-0" style="width:50%;max-width:350px;">
                                <div class="relative">
                                    <img
                                        class="rounded-none shadow-none mb-1"
                                        :src="change.current.image_path || '/images/default.png'"
                                        :alt="'F' + change.current.FLM_ORD_NO + '-' + change.current.FRM"
                                        style="width: 100%;"
                                    />
                                    <div class="absolute bottom-3 w-full text-center text-leonard-white font-bold leading-none text-shadow" v-if="change.current.FLM_ORD_NO">
                                        {{parseInt(change.current.FLM_ORD_NO).toString().substring(-2) + '-' + parseInt(change.current.FRM).toString().padStart(3, '0')}}
                                    </div>
                                </div>
                                <figcaption class="text-center mb-3">
                                    <div class="text-green-600" v-if="studentSelection(change.current, change.type) && isEmployee">
                                        Student Selection
                                    </div>
                                    <div class="text-blue-600" v-if="adminSelection(change.current, change.type) && isEmployee">
                                        Leonard's Selection
                                    </div>
                                    <div class="text-center text-red-600" v-if="!userSelected(change.current, change.type) && change.current.FLM_ORD_NO">
                                        <div>Update</div>
                                        <div v-if="pastCutoff" class="block italic text-sm text-gray-600">
                                            Preview Only: Changes cannot be saved!
                                        </div>
                                    </div>
                                </figcaption>
                            </figure>
                            <div class="ml-3 flex-grow" v-if="retouchingProducts.length > 0 && !loadingRetouching">
                                <div class="text-lg uppercase text-center mb-3">Retouching</div>
                                <div
                                    class="mb-4"
                                    v-for="({prod_code, net_price, flyername, options}, i) in retouchingProducts.filter(p => parseInt(p.QTY_PRINTS) === 1)"
                                    :key="prod_code"
                                >
                                    <div class="relative flex items-start">
                                        <div class="flex items-center h-5">
                                            <input
                                                :id="'options_'+prod_code"
                                                type="checkbox"
                                                class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                                                v-model="retouchingProducts[i].selected"
                                            />
                                        </div>
                                        <div class="ml-3 text-sm leading-5">
                                            <label
                                                :for="'options_'+prod_code"
                                                class="font-medium text-gray-700"
                                            >{{flyername}} - ${{net_price}}</label>
                                        </div>
                                        <div class="ml-3">
                                            <font-awesome-icon
                                                :icon="['far', 'circle-question']"
                                                class="text-leonard-green"
                                                data-toggle="popover"
                                                data-placement="top"
                                                data-trigger="click hover"
                                            ></font-awesome-icon>
                                        </div>
                                    </div>
                                    <div
                                        class="ml-4 mt-2"
                                        v-for="({id, NAME, CONT_CODE}, j) in options"
                                        :key="id"
                                    >
                                        <div class="relative flex items-start ml-4">
                                            <div class="flex items-center h-5">
                                                <input
                                                    :id="'options_'+CONT_CODE"
                                                    type="checkbox"
                                                    class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out disabled:bg-gray-200"
                                                    :disabled="!retouchingProducts[i].selected"
                                                    v-model="retouchingProducts[i].options[j].selected"
                                                />
                                            </div>
                                            <div class="ml-3 text-sm leading-5">
                                                <label
                                                    :for="'options_'+CONT_CODE"
                                                    class="font-medium text-gray-700"
                                                >{{NAME}}</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="retouchingProducts.lenght === 0 && !loadingRetouching">
                                <div class="text-lg text-center text-gray-600">
                                    No Retouching Products Available
                                </div>
                            </div>
                            <div v-else>
                                <div class="text-lg text-center text-gray-600">
                                    <font-awesome-icon icon="spinner" pulse></font-awesome-icon> Loading Retouching Options...
                                </div>
                            </div>
                        </div>
                        <p class="text-center m-0 text-sm text-gray-700">Click a pose below to choose a {{change.type}} pose.</p>
                        <div
                            id="pose-selections"
                            class="bg-gray-50 shadow-inner w-full overflow-auto px-4"
                        >
                            <div class="grid grid-cols-5 gap-2 py-2">
                                <figure
                                    class="border-2 relative"
                                    :class="{'border-gray-900': !userSelected(pose, change.type), 'border-blue-600': userSelected(pose, change.type), 'border-red-600': !userSelected(pose, change.type) && pose.FLM_ORD_NO === change.current.FLM_ORD_NO && pose.FRM === change.current.FRM}"
                                    v-for="(pose, index) in change.available"
                                    :key="index"
                                    @click="changePose(pose)"
                                >
                                    <div class="relative">
                                        <img
                                            class="rounded-0 shadow-none w-full"
                                            :src="pose.image_path || '/images/default.png'"
                                            :alt="'F' + pose.FLM_ORD_NO + '-' + pose.FRM"
                                        />
                                        <div class="absolute bottom-3 w-full text-center text-white text-sm font-bold leading-none text-shadow">{{parseInt(pose.FLM_ORD_NO).toString().substr(-2) + '-' + parseInt(pose.FRM).toString().padStart(3, '0')}}</div>
                                    </div>
                                    <div
                                        class="absolute top-0 right-0 selection-icon"
                                        :class="{'admin-selection': adminSelection(pose, change.type), 'student-selection': studentSelection(pose, change.type)}"
                                    >
                                        <font-awesome-layers
                                            class="admin fa-lg"
                                            v-show="adminSelection(pose, change.type) && isEmployee"
                                        >
                                            <font-awesome-icon icon="certificate" class="text-blue-600"></font-awesome-icon>
                                            <font-awesome-icon
                                                icon="check"
                                                transform="shrink-8"
                                                class="text-white"
                                            ></font-awesome-icon>
                                        </font-awesome-layers>
                                        <font-awesome-layers
                                            class="student ml-1 fa-lg"
                                            v-show="studentSelection(pose, change.type)"
                                        >
                                            <font-awesome-icon
                                                icon="certificate"
                                                class="text-green-600"
                                            ></font-awesome-icon>
                                            <font-awesome-icon
                                                icon="check"
                                                transform="shrink-8"
                                                class="text-white"
                                            ></font-awesome-icon>
                                        </font-awesome-layers>
                                    </div>
                                </figure>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="bg-gray-100 text-right py-4 px-2 rounded-b-md">
                    <button
                        type="button"
                        class="btn text-gray-700 border border-gray-600 hover:bg-gray-600 hover:text-white"
                        data-dismiss="modal"
                    >Cancel</button>
                    <button
                        type="button"
                        class="btn px-6 text-white border border-blue-500 hover:border-blue-700 bg-blue-500 hover:bg-blue-700 disabled:opacity-75"
                        data-dismiss="modal"
                        @click="savePose(change.type)"
                        :disabled="saving"
                        v-if="!pastCutoff"
                    >Save</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCertificate, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons'
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'

import Alert from '../../../components/app/Alert'
import Order from '../../../classes/Order'
import Retouching from '../../../classes/Retouching'

export default {
    props: ['pastCutoff', 'change', 'hasFormalRetouching', 'hasCasualRetouching'],

    components: {
        Alert,
    },

    data() {
        return {
            Retouching: new Retouching(),
            Order: new Order(),
            saving: false,
            retouchingProducts: [],
            hasRetouching: {},
            loadingRetouching: false,
        }
    },

    created() {
        library.add(faCertificate, faCheck, faXmark, faCircleQuestion, faSpinner)
    },

    mounted() {
        this.getRetouching()
    },

    updated() {
        this.hasRetouching = this.change.type === 'formal' ? this.hasFormalRetouching : this.hasCasualRetouching
        this.initializePopover()
    },

    beforeDestroy() {
        $('[data-toggle="popover"]').popover('dispose')
    },

    watch: {
        hasRetouching: function (newValue, oldValue) {
            if (newValue !== oldValue) {
                this.getRetouching()
            }
        }
    },

    methods: {
        initializePopover() {
            $('[data-toggle="popover"]').popover({
                html: true,
                content: `<ul class="list-disc list-outside m-0 p-0 px-3">
                              <li>Remove Blemishes, facial lines, dark under-eye circles</li>
                              <li>Remove stray hairs</li>
                              <li>Remove tan lines</li>
                              <li>Whiten teeth & eyes</li>
                          </ul>`,
            })
        },

        getRetouching() {
            let self = this
            let data = {
                con_no: localStorage.getItem('con_no'),
            }

            this.loadingRetouching = true
            this.Retouching.getRetouching(data).then(function (response) {
                self.retouchingProducts = response.data
            }).then(() => {
                self.loadingRetouching = false
                if(self.hasRetouching && self.retouchingProducts.length > 0) {
                    // check the retouching checkbox
                    self.retouchingProducts[0].selected = true
                    // check off the retouching products in the item contents
                    let contents = JSON.parse(self.hasRetouching.contents)
                    self.retouchingProducts[0].options.forEach(option => {
                        contents.forEach(content => {
                            if(content === option.NAME) {
                                option.selected = true
                            }
                        })
                    })
                }
            })
        },

        changePose(pose) {
            this.change.current = pose
        },

        adminSelection(pose, type) {
            let match = this.change.available.findIndex(
                p => p.FLM_ORD_NO === pose.FLM_ORD_NO && p.FRM === pose.FRM
            )
            if (match === -1) return false

            return (
                ((type === 'formal' &&
                    this.change.available[match].YBKPOSE2 === 1) ||
                    (type === 'casual' &&
                        this.change.available[match].YBKPOSE4 === 1)) &&
                this.isEmployee
            )
        },

        studentSelection(pose, type) {
            let match = this.change.available.findIndex(
                p => p.FLM_ORD_NO === pose.FLM_ORD_NO && p.FRM === pose.FRM
            )
            if (match === -1) return false

            return (
                (type === 'formal' &&
                    this.change.available[match].YBKPOSE === 1) ||
                (type === 'casual' &&
                    this.change.available[match].YBKPOSE3 === 1)
            )
        },

        userSelected(pose, type) {
            const matchFlmFrm =
                pose.FLM_ORD_NO === this.change.current.FLM_ORD_NO &&
                pose.FRM === this.change.current.FRM
            if (this.isEmployee) {
                return this.adminSelection(pose, type) && matchFlmFrm
            } else {
                return this.studentSelection(pose, type) && matchFlmFrm
            }
        },

        savePose(type) {
            this.saving = true

            let self = this
            let data = {
                user_id: this.$auth.user().details.id,
                film: this.change.current.FLM_ORD_NO,
                frame: this.change.current.FRM,
                pose:
                    type === 'casual'
                        ? ['YBKPOSE3', 'YBKPOSE4']
                        : ['YBKPOSE', 'YBKPOSE2'],
                recpt_no: localStorage.getItem('access_code'),
                con_no: localStorage.getItem('con_no'),
                school: localStorage.getItem('school'),
                employee: this.isEmployee.toString(),
                retouching: this.retouchingProducts,
            }

            this.Order.updatePose(data)
                .then(function (response) {
                    if (!response.success) return

                    self.$emit(
                        'savePose',
                        self.change.type,
                        self.change.current,
                        data.pose
                    )

                    feedback('Pose saved')

                    self.saving = false

                    $('#yearbook_pose').modal('hide')
                })
                .then(function () {
                    window.events.$emit('refreshCart')
                })
        },
    },
}
</script>

<style lang="scss" scoped>
.btn[disabled],
.btn.disabled {
    cursor: not-allowed;
}

.text-shadow {
    text-shadow: 0 0 4px #000;
}

#yearbook_pose {
    figcaption {
        position: relative;
        bottom: unset;
        color: unset;
        text-shadow: none;
        text-transform: uppercase;
    }

    #pose-selections {
        max-height: 280px;
        scrollbar-color: rgba(55, 65, 81, 1) transparent;
        scrollbar-width: thin;

        figure {
            transition: none;
            max-width: 175px;
            cursor: pointer;
        }

        &::-webkit-scrollbar {
            background: rgba(249, 250, 251, 1);
            width: 10px;
        }
        &::-webkit-scrollbar-thumb {
            border-radius: 1rem;
            background: rgba(55, 65, 81, 1);
        }
    }

    .selection-icon {
        .fa-layers {
            padding: 3px;

            svg:first-of-type {
                filter: drop-shadow(1px 1px 2px #373737);
            }
        }
    }
}
</style>
